import React, { useState, useEffect, useMemo } from 'react';
import { tournamentApi, eventApi } from '../../../services/api';

// Derive a human-readable round label from the match count within that round for one event
const getRoundLabel = (roundNum, evMatchList) => {
  const cnt = evMatchList.filter(m => m.round_number === roundNum).length;
  if (cnt === 1) return 'Final';
  if (cnt === 2) return 'Semi Finals';
  if (cnt === 4) return 'Quarter Finals';
  if (cnt === 8) return 'Round of 16';
  if (cnt === 16) return 'Round of 32';
  return `Round ${roundNum}`;
};

const ScheduleModal = ({ tournament, matches, onClose, onSaved }) => {
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [scheduleForm, setScheduleForm] = useState({
    start_date: '', end_date: '', start_time: '09:00', end_time: '21:00',
    match_duration: 15,
    match_gap: 5, round_gap: 0, num_courts: 4,
    venue: tournament?.venue || '',
    scheduling_mode: 'parallel',
    event_ids: [],
    scoring: {
      knockout: { no_of_sets: 'best_of_3', points_per_set: 15 },
      quarter_final: { no_of_sets: 'best_of_3', points_per_set: 15 },
      semi_final: { no_of_sets: 'best_of_3', points_per_set: 21 },
      final: { no_of_sets: 'best_of_3', points_per_set: 21 }
    }
  });
  // { [eventId]: number[] } — selected round numbers per event
  const [eventRounds, setEventRounds] = useState({});
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!tournament?.id) return;
    setEventsLoading(true);
    eventApi.list(tournament.id)
      .then(res => {
        const evs = res.data || [];
        setEvents(evs);
        setScheduleForm(prev => ({ ...prev, event_ids: evs.map(e => e.id) }));
      })
      .catch(() => {})
      .finally(() => setEventsLoading(false));
  }, [tournament?.id]);

  // Compute available rounds per event from the matches prop
  const eventRoundOptions = useMemo(() => {
    const result = {};
    for (const ev of events) {
      const evMatches = (matches || []).filter(m => m.event_id === ev.id);
      const rounds = [...new Set(evMatches.map(m => m.round_number))].sort((a, b) => a - b);
      result[ev.id] = rounds;
    }
    return result;
  }, [events, matches]);

  // When events (or their round options) change, default all rounds selected
  useEffect(() => {
    const initial = {};
    for (const ev of events) {
      initial[ev.id] = [...(eventRoundOptions[ev.id] || [])];
    }
    setEventRounds(initial);
  }, [events, eventRoundOptions]);

  const set = (key, value) => setScheduleForm(prev => ({ ...prev, [key]: value }));
  const setScoring = (round, key, value) => setScheduleForm(prev => ({
    ...prev,
    scoring: { ...prev.scoring, [round]: { ...prev.scoring[round], [key]: value } }
  }));

  const toggleEvent = (eventId) => {
    setScheduleForm(prev => {
      const ids = prev.event_ids.includes(eventId)
        ? prev.event_ids.filter(id => id !== eventId)
        : [...prev.event_ids, eventId];
      return { ...prev, event_ids: ids };
    });
  };

  const toggleRound = (eventId, roundNum) => {
    setEventRounds(prev => {
      const current = prev[eventId] || [];
      const updated = current.includes(roundNum)
        ? current.filter(r => r !== roundNum)
        : [...current, roundNum].sort((a, b) => a - b);
      return { ...prev, [eventId]: updated };
    });
  };

  const selectAllRounds = (eventId) => {
    setEventRounds(prev => ({ ...prev, [eventId]: [...(eventRoundOptions[eventId] || [])] }));
  };

  const handleSubmit = async () => {
    if (!scheduleForm.start_date || !scheduleForm.end_date) { alert('Select start and end dates'); return; }
    if (scheduleForm.event_ids.length === 0) { alert('Select at least one event to schedule'); return; }
    // Validate at least one round selected per selected event
    for (const evId of scheduleForm.event_ids) {
      if (!eventRounds[evId] || eventRounds[evId].length === 0) {
        const evName = events.find(e => e.id === evId)?.name || 'an event';
        alert(`Select at least one round for "${evName}"`);
        return;
      }
    }

    // Build event_rounds payload (only for selected events)
    const event_rounds = {};
    for (const evId of scheduleForm.event_ids) {
      event_rounds[evId] = eventRounds[evId] || [];
    }

    setGenerating(true);
    try {
      await tournamentApi.generateSchedule(tournament.id, { ...scheduleForm, event_rounds });
      onSaved();
    } catch (err) { alert(err.response?.data?.error || 'Failed to generate schedule'); }
    finally { setGenerating(false); }
  };

  const isBadminton = tournament?.sport_type === 'badminton';
  const multipleEventsSelected = scheduleForm.event_ids.length > 1;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '640px', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3>Create Schedule</h3>

        {/* ── Events + Rounds ─────────────────────────────────── */}
        <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#F0FDFA', borderRadius: '4px', border: '1px solid #99F6E4' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <label className="form-label" style={{ margin: 0, fontWeight: 600 }}>Events & Rounds to Schedule</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" className="btn btn-outline btn-sm"
                style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                onClick={() => set('event_ids', events.map(e => e.id))}>All Events</button>
              <button type="button" className="btn btn-outline btn-sm"
                style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                onClick={() => set('event_ids', [])}>None</button>
            </div>
          </div>

          {eventsLoading ? (
            <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: 0 }}>Loading events…</p>
          ) : events.length === 0 ? (
            <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: 0 }}>No events found. Create events first.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {events.map(ev => {
                const isSelected = scheduleForm.event_ids.includes(ev.id);
                const rounds = eventRoundOptions[ev.id] || [];
                const selectedRounds = eventRounds[ev.id] || [];
                const evMatches = (matches || []).filter(m => m.event_id === ev.id);

                return (
                  <div key={ev.id} style={{
                    border: `1px solid ${isSelected ? '#5EEAD4' : '#e5e7eb'}`,
                    borderRadius: '4px',
                    background: isSelected ? '#fff' : '#f9fafb',
                    overflow: 'hidden'
                  }}>
                    {/* Event header checkbox */}
                    <label style={{
                      display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer',
                      padding: '0.5rem 0.75rem', background: isSelected ? '#CCFBF1' : '#f3f4f6'
                    }}>
                      <input type="checkbox" checked={isSelected} onChange={() => toggleEvent(ev.id)} />
                      <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{ev.name}</span>
                      {ev.event_type && (
                        <span style={{ fontSize: '0.7rem', padding: '1px 6px', background: '#99F6E4', color: '#115E59', borderRadius: 999 }}>
                          {ev.event_type}
                        </span>
                      )}
                      {ev.age_category && (
                        <span style={{ fontSize: '0.7rem', padding: '1px 6px', background: '#fef9c3', color: '#92400e', borderRadius: 999 }}>
                          {ev.age_category}
                        </span>
                      )}
                      {rounds.length === 0 && (
                        <span style={{ fontSize: '0.7rem', color: '#ef4444', marginLeft: 'auto' }}>No draw generated</span>
                      )}
                    </label>

                    {/* Round checkboxes — only shown when event is selected and has rounds */}
                    {isSelected && rounds.length > 0 && (
                      <div style={{ padding: '0.5rem 0.75rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
                          <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Rounds to include:</span>
                          <button type="button"
                            style={{ fontSize: '0.7rem', color: '#0D9488', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                            onClick={() => selectAllRounds(ev.id)}>Select all</button>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                          {rounds.map(r => {
                            const label = getRoundLabel(r, evMatches);
                            const checked = selectedRounds.includes(r);
                            return (
                              <label key={r} style={{
                                display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer',
                                padding: '3px 8px', borderRadius: 999, fontSize: '0.8rem',
                                border: `1px solid ${checked ? '#0D9488' : '#d1d5db'}`,
                                background: checked ? '#CCFBF1' : '#fff',
                                color: checked ? '#115E59' : '#374151'
                              }}>
                                <input type="checkbox" checked={checked} onChange={() => toggleRound(ev.id, r)}
                                  style={{ width: '12px', height: '12px' }} />
                                {label}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Date / time ──────────────────────────────────────── */}
        <div className="grid grid-2">
          <div className="form-group"><label className="form-label">Start Date *</label><input type="date" className="form-input" value={scheduleForm.start_date} onChange={(e) => set('start_date', e.target.value)} /></div>
          <div className="form-group"><label className="form-label">End Date *</label><input type="date" className="form-input" value={scheduleForm.end_date} onChange={(e) => set('end_date', e.target.value)} /></div>
        </div>
        <div className="grid grid-2">
          <div className="form-group"><label className="form-label">Start Time *</label><input type="time" className="form-input" value={scheduleForm.start_time} onChange={(e) => set('start_time', e.target.value)} /></div>
          <div className="form-group"><label className="form-label">End Time *</label><input type="time" className="form-input" value={scheduleForm.end_time} onChange={(e) => set('end_time', e.target.value)} /></div>
        </div>
        <div className="grid grid-2">
          <div className="form-group"><label className="form-label">Match Duration (min) *</label><input type="number" className="form-input" value={scheduleForm.match_duration} onChange={(e) => set('match_duration', e.target.value)} min="1" /></div>
          <div className="form-group"><label className="form-label">Interval between matches (min)</label><input type="number" className="form-input" value={scheduleForm.match_gap} onChange={(e) => set('match_gap', e.target.value)} min="0" /></div>
        </div>
        <div className="grid grid-2">
          <div className="form-group"><label className="form-label">Number of Courts *</label><input type="number" className="form-input" value={scheduleForm.num_courts} onChange={(e) => set('num_courts', e.target.value)} min="1" /></div>
          <div className="form-group"><label className="form-label">Round Gap (min)</label><input type="number" className="form-input" value={scheduleForm.round_gap} onChange={(e) => set('round_gap', e.target.value)} min="0" /></div>
        </div>

        {/* ── Venue ───────────────────────────────────────────── */}
        <div className="form-group">
          <label className="form-label">Venue</label>
          <input
            type="text"
            className="form-input"
            value={scheduleForm.venue}
            onChange={(e) => set('venue', e.target.value)}
            placeholder={tournament?.venue || 'e.g. Main Badminton Hall, Court 1–4'}
          />
        </div>

        {/* ── Court assignment mode (multi-event only) ─────────── */}
        {multipleEventsSelected && (
          <div className="form-group">
            <label className="form-label">Court Assignment Mode</label>
            <select className="form-select" value={scheduleForm.scheduling_mode} onChange={(e) => set('scheduling_mode', e.target.value)}>
              <option value="parallel">Parallel — fill courts as available</option>
              <option value="slot">Fixed Slots — all courts start together</option>
            </select>
          </div>
        )}

        {/* ── Badminton scoring by round ───────────────────────── */}
        {isBadminton && (
          <div style={{ marginTop: '1rem', padding: '1rem', background: '#f5f5f5', borderRadius: '4px' }}>
            <h4 style={{ marginBottom: '0.75rem' }}>Badminton Scoring by Round</h4>
            {[
              { key: 'knockout', label: 'Early Rounds' },
              { key: 'quarter_final', label: 'Quarter Finals' },
              { key: 'semi_final', label: 'Semi Finals' },
              { key: 'final', label: 'Final' }
            ].map(({ key, label }) => (
              <div key={key} style={{ marginBottom: '1rem' }}>
                <label className="form-label" style={{ fontWeight: 'bold' }}>{label}</label>
                <div className="grid grid-2">
                  <div className="form-group">
                    <label className="form-label">No. of Sets</label>
                    <select className="form-select" value={scheduleForm.scoring[key]?.no_of_sets || 'best_of_3'} onChange={(e) => setScoring(key, 'no_of_sets', e.target.value)}>
                      <option value="best_of_1">Best of 1</option>
                      <option value="best_of_3">Best of 3</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Points per Set</label>
                    <select className="form-select" value={scheduleForm.scoring[key]?.points_per_set} onChange={(e) => setScoring(key, 'points_per_set', parseInt(e.target.value))}>
                      <option value={15}>15 Points</option>
                      <option value={21}>21 Points</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Footer ──────────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={generating || !scheduleForm.start_date || !scheduleForm.end_date || scheduleForm.event_ids.length === 0}
          >
            {generating ? 'Generating…' : `Generate Schedule${scheduleForm.event_ids.length > 0 ? ` (${scheduleForm.event_ids.length} event${scheduleForm.event_ids.length > 1 ? 's' : ''})` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleModal;
